// Item/Product Service
// CRUD operations and business logic for inventory items
// OFFLINE-FIRST: Always save locally first, sync silently when online

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  DocumentData,
  QueryDocumentSnapshot,
  Unsubscribe
} from 'firebase/firestore'
import { auth, db, COLLECTIONS, getFirebaseReadinessErrorMessage, isFirebaseReady } from './firebase'
import type { Item } from '../types'
import {
  saveToOffline,
  getAllFromOffline,
  getFromOffline,
  deleteFromOffline,
  addToSyncQueue,
  registerSyncCallback,
  isDeviceOnline,
  STORES
} from './offlineSyncService'
import { getFromCache, setInCache, invalidateCacheByPrefix } from '../utils/cacheUtils'
import { DATA_EVENTS, emitDataEvent } from '../utils/dataEvents'
import { getCurrentCompanyId, getCurrentUserId, getScopedStorageKey } from '../utils/authUtils'
import { getLocalJSON, getMutableLocalJSON, markLocalJSONDirty } from '../utils/localJsonStore'

const LOCAL_STORAGE_KEY = 'thisai_crm_items'
const getLocalStorageKey = () => getScopedStorageKey(LOCAL_STORAGE_KEY, 'company')

export const normalizeBarcodeValue = (value: unknown): string =>
  String(value ?? '')
    .replace(/[\x00-\x1F\x7F]/g, '')
    .trim()

const getBarcodeLookupVariants = (value: unknown): string[] => {
  const normalized = normalizeBarcodeValue(value)
  if (!normalized) return []

  const compact = normalized.replace(/[\s-]+/g, '')
  const variants = new Set<string>([normalized])

  if (compact) variants.add(compact)
  if (/^0\d{12}$/.test(compact)) variants.add(compact.slice(1))
  if (/^\d{12}$/.test(compact)) variants.add(`0${compact}`)

  return Array.from(variants)
}

// Helper to generate offline-safe ID
const generateOfflineId = () => `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

// Check if ID is offline-generated
const isOfflineId = (id: string) => id.startsWith('item_') || id.startsWith('offline_')

// Duplicate-submit guard for slow UI/network conditions.
// Prevents duplicate items when users click "Save" repeatedly.
const ITEM_CREATE_DEDUP_WINDOW_MS = 8000
const inFlightItemCreateKeys = new Map<string, Promise<Item | null>>()
const recentCreatedItems = new Map<string, { item: Item; timestamp: number }>()

const normalizeItemDedupPart = (value: unknown): string =>
  String(value ?? '').trim().toLowerCase()

const normalizeMoneyForItemDedup = (value: unknown): string => {
  const n = Number(value)
  if (!Number.isFinite(n)) return '0.00'
  return (Math.round(n * 100) / 100).toFixed(2)
}

const buildItemCreateDedupKey = (
  itemData: Partial<Item>,
  companyId: string
): string => {
  return [
    companyId,
    normalizeItemDedupPart((itemData as any).name),
    normalizeItemDedupPart((itemData as any).barcode),
    normalizeItemDedupPart((itemData as any).hsnCode),
    normalizeMoneyForItemDedup((itemData as any).purchasePrice),
    normalizeMoneyForItemDedup((itemData as any).sellingPrice),
    normalizeItemDedupPart((itemData as any).unit),
  ].join('|')
}

const purgeRecentCreatedItems = () => {
  const now = Date.now()
  for (const [key, entry] of recentCreatedItems.entries()) {
    if (now - entry.timestamp > ITEM_CREATE_DEDUP_WINDOW_MS) {
      recentCreatedItems.delete(key)
    }
  }
}

const sortItemsNewestFirst = (items: Item[]): Item[] => {
  return items.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
}

/**
 * Helper function to deeply remove undefined values from an object
 */
function removeUndefinedDeep(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedDeep(item))
  }

  if (typeof obj === 'object') {
    const cleaned: any = {}
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedDeep(value)
      }
    }
    return cleaned
  }

  return obj
}

/**
 * Get all items
 * Production read: Firestore is the source of truth for shared inventory.
 * OPTIMIZED: Uses caching to reduce redundant database calls
 */
export async function getItems(): Promise<Item[]> {
  const companyId = getCurrentCompanyId()
  if (!companyId) {
    console.warn('[getItems] Missing companyId - returning empty list')
    return []
  }

  // Check cache only for an actual offline session. Online callers should see
  // the shared cloud state, not stale browser-local data.
  const cacheKey = `items_all_${companyId}`
  const cached = getFromCache<Item[]>(cacheKey, companyId)
  if (cached && !isDeviceOnline()) {
    console.log('[getItems] Cache hit:', cached.length, 'items')
    return cached
  }

  console.log('📥 itemService.getItems called')

  // STEP 1: Always try IndexedDB first (instant, works offline)
  let localItems: Item[] = []
  try {
    localItems = await getAllFromOffline<Item>(STORES.ITEMS)
    localItems = localItems.filter(item => (item as any).companyId === companyId)
    const localStorageItems = getItemsFromLocalStorage()
    if (localStorageItems.length > 0) {
      const byId = new Map<string, Item>()
      localItems.forEach(item => byId.set(item.id, item))
      localStorageItems.forEach(item => {
        if (!byId.has(item.id)) {
          byId.set(item.id, item)
          saveToOffline(STORES.ITEMS, item).catch(() => {})
        }
      })
      localItems = Array.from(byId.values())
    }
    // Sort by createdAt descending (newest first)
    localItems.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  } catch (error) {
    console.warn('IndexedDB read failed, trying localStorage:', error)
    localItems = getItemsFromLocalStorage()
  }

  // STEP 2: In production, do not silently replace Firestore with local/demo data.
  if (!isFirebaseReady()) {
    throw new Error(getFirebaseReadinessErrorMessage())
  }

  if (!isDeviceOnline()) {
    console.warn('Inventory Firestore read blocked because the browser is offline')
    throw new Error('You are offline. Connect to the internet to load shared inventory items.')
  }

  // STEP 3: If online, try to fetch from Firebase and merge
  try {
    const itemsRef = collection(db!, COLLECTIONS.ITEMS)
    const companyId = getCurrentCompanyId()

    // Use query with companyId filter for data isolation
    const q = query(itemsRef, where('companyId', '==', companyId))
    console.info('[POS Firebase Debug] getItems Firestore fetch', {
      authUid: auth?.currentUser?.uid || null,
      companyId,
      businessId: companyId,
      firebaseReady: isFirebaseReady(),
      itemsCollectionPath: `${COLLECTIONS.ITEMS} where companyId == "${companyId}"`
    })

    const snapshot = await getDocs(q)
    let serverItems = snapshot.docs.map(docToItem)
    // Sort client-side instead
    serverItems.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    // Firestore is the production source of truth. Do not merge browser-local
    // items into live inventory reads.
    const localOnlyItems: Item[] = []
    const mergedItems = serverItems

    // Update local cache with server data (in background)
    for (const item of serverItems) {
      saveToOffline(STORES.ITEMS, item).catch(() => {})
    }

    console.log('☁️ Retrieved from Firebase:', serverItems.length, 'items, merged with', localOnlyItems.length, 'local')
    const sortedItems = mergedItems.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    // Update cache
    setInCache(cacheKey, sortedItems, companyId)

    return sortedItems
  } catch (error: any) {
    console.error('FIRESTORE ERROR CODE:', error?.code || null)
    console.error('FIRESTORE ERROR MESSAGE:', error?.message || String(error))
    console.error('FULL ERROR:', error)
    console.warn('Firebase fetch failed:', error)
    throw error
  }
}

/**
 * Subscribe to the authoritative Firestore item list for a company.
 * POS uses this so products are shared across browsers/devices instead of
 * depending on per-browser localStorage/IndexedDB state.
 */
export function subscribeItemsForCompany(
  companyId: string,
  onItems: (items: Item[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const itemsCollectionPath = COLLECTIONS.ITEMS
  const itemsQueryPath = `${itemsCollectionPath} where companyId == "${companyId}"`

  if (!companyId) {
    console.error('[POS Firebase Debug] Firestore items subscription skipped', {
      reason: 'Missing companyId',
      itemsCollectionPath,
      itemsQueryPath
    })
    onError?.(new Error('Missing companyId'))
    return () => {}
  }

  if (!isFirebaseReady() || !db) {
    const error = new Error(getFirebaseReadinessErrorMessage())
    console.error('[POS Firebase Debug] Firestore items subscription skipped', {
      reason: error.message,
      hasDb: Boolean(db),
      firebaseReady: isFirebaseReady(),
      itemsCollectionPath,
      itemsQueryPath
    })
    onError?.(error)
    return () => {}
  }

  console.info('[POS Firebase Debug] Firestore items subscription starting', {
    companyId,
    itemsCollectionPath,
    itemsQueryPath
  })

  const q = query(
    collection(db, COLLECTIONS.ITEMS),
    where('companyId', '==', companyId)
  )

  return onSnapshot(
    q,
    (snapshot) => {
      console.info('[POS Firebase Debug] Firestore items snapshot received', {
        companyId,
        itemsCollectionPath,
        itemsQueryPath,
        size: snapshot.size,
        sampleDocs: snapshot.docs.slice(0, 5).map((docSnap) => {
          const data = docSnap.data() as any
          return {
            id: docSnap.id,
            name: data.name || null,
            companyId: data.companyId || null,
            businessId: data.businessId || null,
            price: data.price ?? data.sellingPrice ?? data.salePrice ?? null,
            stock: data.stock ?? data.quantity ?? null,
            isActive: data.isActive ?? null
          }
        })
      })

      const serverItems = sortItemsNewestFirst(snapshot.docs.map(docToItem))

      for (const item of serverItems) {
        saveToOffline(STORES.ITEMS, item).catch(() => {})
        saveItemToLocalStorage(item)
      }

      setInCache(`items_all_${companyId}`, serverItems, companyId)
      onItems(serverItems)
    },
    (error) => {
      console.error('FIRESTORE ERROR CODE:', (error as any)?.code || null)
      console.error('FIRESTORE ERROR MESSAGE:', error.message || String(error))
      console.error('FULL ERROR:', error)
      console.error('[POS Firebase Debug] Firestore items listener failed', {
        companyId,
        itemsCollectionPath,
        itemsQueryPath,
        code: (error as any)?.code || null,
        message: error.message || String(error),
        error
      })
      onError?.(error)
    }
  )
}

/**
 * Strict cloud create for live POS/inventory flows.
 * Unlike createItem(), this does not silently fall back to local-only storage.
 */
export async function createItemInFirestore(
  itemData: Omit<Item, 'id' | 'createdAt' | 'updatedAt'>,
  companyIdOverride?: string
): Promise<Item> {
  const companyId = companyIdOverride || getCurrentCompanyId()
  if (!companyId) {
    throw new Error('Missing companyId')
  }

  if (!isFirebaseReady() || !db) {
    throw new Error(getFirebaseReadinessErrorMessage())
  }

  if (!isDeviceOnline()) {
    throw new Error('You are offline. Connect to the internet before adding POS items.')
  }

  const now = new Date().toISOString()
  const createdBy = getCurrentUserId()
  const normalizedName = String((itemData as any).name || '').trim().toLowerCase()
  const normalizedBarcode = String((itemData as any).barcode || '').trim().toLowerCase()

  const existingSnapshot = await getDocs(query(collection(db, COLLECTIONS.ITEMS), where('companyId', '==', companyId)))
  const duplicate = existingSnapshot.docs.find((docSnap) => {
    const existing = docSnap.data() as any
    const existingName = String(existing.name || '').trim().toLowerCase()
    const existingBarcode = String(existing.barcode || '').trim().toLowerCase()

    if (normalizedBarcode && existingBarcode && normalizedBarcode === existingBarcode) return true
    return normalizedName.length > 0 && existingName === normalizedName
  })

  if (duplicate) {
    throw new Error('An item with the same name or barcode already exists in Firebase.')
  }

  const cleanServerData = removeUndefinedDeep({
    ...itemData,
    companyId,
    price: (itemData as any).price ?? (itemData as any).sellingPrice ?? (itemData as any).salePrice ?? (itemData as any).retailPrice,
    createdBy: createdBy || undefined,
    isActive: (itemData as any).isActive !== false
  })
  const serverData = {
    ...cleanServerData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }

  const docRef = await addDoc(collection(db, COLLECTIONS.ITEMS), serverData)
  const created = {
    ...cleanServerData,
    id: docRef.id,
    createdAt: now,
    updatedAt: now
  } as Item

  await saveToOffline(STORES.ITEMS, created).catch(() => {})
  saveItemToLocalStorage(created)
  invalidateCacheByPrefix('items')
  emitDataEvent(DATA_EVENTS.ITEMS_CHANGED, { action: 'create', id: created.id })

  return created
}

export function getLocalItemsForCompany(companyId: string): Item[] {
  if (!companyId) return []

  try {
    const storageKey = `${LOCAL_STORAGE_KEY}_${companyId}`
    const scopedStored = getLocalJSON<Item[]>(storageKey, [])
    const legacyStored = getLocalJSON<Item[]>(LOCAL_STORAGE_KEY, [])
    const byId = new Map<string, Item>()

    const addCandidate = (item: Item) => {
      const itemCompanyId = (item as any).companyId
      if (itemCompanyId && itemCompanyId !== companyId) return
      const normalized = { ...item, companyId } as Item
      byId.set(normalized.id, normalized)
    }

    if (Array.isArray(scopedStored)) {
      scopedStored.forEach(addCandidate)
    }
    if (Array.isArray(legacyStored)) {
      legacyStored.forEach(addCandidate)
    }

    return sortItemsNewestFirst(Array.from(byId.values()))
  } catch (error) {
    console.error('Error reading company-scoped local items:', error)
    return []
  }
}

export async function migrateLocalItemsToFirestore(
  companyId: string = getCurrentCompanyId() || ''
): Promise<{ created: number; skipped: number; failed: number }> {
  if (!companyId) throw new Error('Missing companyId')
  if (!isFirebaseReady() || !db) throw new Error(getFirebaseReadinessErrorMessage())
  if (!isDeviceOnline()) throw new Error('You are offline')

  const localItems = getLocalItemsForCompany(companyId)
  const snapshot = await getDocs(query(collection(db, COLLECTIONS.ITEMS), where('companyId', '==', companyId)))
  const existingKeys = new Set(
    snapshot.docs.map((docSnap) => {
      const data = docSnap.data() as Item
      return [
        String((data as any).barcode || '').trim().toLowerCase(),
        String(data.name || '').trim().toLowerCase(),
        String(data.sellingPrice ?? data.salePrice ?? data.retailPrice ?? 0)
      ].join('|')
    })
  )

  let created = 0
  let skipped = 0
  let failed = 0

  for (const item of localItems) {
    const key = [
      String((item as any).barcode || '').trim().toLowerCase(),
      String(item.name || '').trim().toLowerCase(),
      String(item.sellingPrice ?? item.salePrice ?? item.retailPrice ?? 0)
    ].join('|')

    if (existingKeys.has(key)) {
      skipped++
      continue
    }

    try {
      const { id: _id, _pendingSync, _savedAt, _syncedAt, ...rest } = item as any
      const migrated = await createItemInFirestore(rest, companyId)
      existingKeys.add(key)
      await deleteFromOffline(STORES.ITEMS, item.id).catch(() => {})
      deleteItemFromLocalStorage(item.id)
      saveItemToLocalStorage(migrated)
      created++
    } catch (error) {
      console.error('[migrateLocalItemsToFirestore] Failed to migrate item:', item, error)
      failed++
    }
  }

  return { created, skipped, failed }
}

/**
 * Get item by ID
 * OFFLINE-FIRST: Checks IndexedDB first, then Firebase
 */
export async function getItemById(id: string): Promise<Item | null> {
  // STEP 1: Try IndexedDB first
  try {
    const localItem = await getFromOffline<Item>(STORES.ITEMS, id)
    if (localItem) {
      return localItem
    }
  } catch (error) {
    console.warn('IndexedDB read failed:', error)
  }

  // STEP 2: Check localStorage fallback
  const localItems = getItemsFromLocalStorage()
  const localStorageItem = localItems.find(i => i.id === id)
  if (localStorageItem) {
    return localStorageItem
  }

  // STEP 3: If offline or Firebase not ready, return null
  if (!isDeviceOnline() || !isFirebaseReady()) {
    return null
  }

  // STEP 4: Try Firebase
  try {
    const docRef = doc(db!, COLLECTIONS.ITEMS, id)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      const item = docToItem(docSnap)
      // Cache in IndexedDB for future offline access
      saveToOffline(STORES.ITEMS, item).catch(() => {})
      return item
    }
    return null
  } catch (error) {
    console.error('Error fetching item:', error)
    return null
  }
}

/**
 * Find item by HSN code
 */
export async function findItemByHSN(hsnCode: string): Promise<Item | null> {
  if (!hsnCode) return null

  const companyId = getCurrentCompanyId()
  if (!companyId) return null

  if (!isFirebaseReady()) {
    const items = getItemsFromLocalStorage()
    return items.find(i => i.hsnCode === hsnCode) || null
  }

  try {
    const itemsRef = collection(db!, COLLECTIONS.ITEMS)
    const q = query(
      itemsRef,
      where('companyId', '==', companyId),
      where('hsnCode', '==', hsnCode)
    )
    const snapshot = await getDocs(q)

    if (snapshot.empty) return null

    return docToItem(snapshot.docs[0])
  } catch (error) {
    console.error('Error finding item by HSN:', error)
    return null
  }
}

/**
 * Find item by barcode
 */
export async function findItemByBarcode(barcode: string): Promise<Item | null> {
  const normalizedBarcode = normalizeBarcodeValue(barcode)
  if (!normalizedBarcode) return null
  const scannedVariants = getBarcodeLookupVariants(normalizedBarcode)
  const scannedVariantSet = new Set(scannedVariants.map(value => value.toLowerCase()))

  const companyId = getCurrentCompanyId()
  if (!companyId) return null

  const matchesBarcode = (item: Item) => {
    const candidates = [
      (item as any).barcode,
      (item as any).itemCode,
      (item as any).sku,
      (item as any).code,
      (item as any).id
    ].flatMap(getBarcodeLookupVariants)

    return candidates.some(value => scannedVariantSet.has(value.toLowerCase()))
  }

  if (!isFirebaseReady()) {
    const items = await getItems()
    return items.find(matchesBarcode) || null
  }

  try {
    const itemsRef = collection(db!, COLLECTIONS.ITEMS)
    const q = query(
      itemsRef,
      where('companyId', '==', companyId),
      where('barcode', '==', normalizedBarcode)
    )
    const snapshot = await getDocs(q)

    if (!snapshot.empty) return docToItem(snapshot.docs[0])

    const items = await getItems()
    return items.find(matchesBarcode) || null
  } catch (error) {
    console.error('Error finding item by barcode:', error)
    const items = await getItems()
    return items.find(matchesBarcode) || null
  }
}

/**
 * Find item by name (fuzzy match)
 */
export async function findItemByName(name: string): Promise<Item | null> {
  if (!name) return null

  const items = await getItems()

  // Try exact match first
  const exactMatch = items.find(i =>
    i.name.toLowerCase() === name.toLowerCase()
  )
  if (exactMatch) return exactMatch

  // Try partial match
  const partialMatch = items.find(i =>
    i.name.toLowerCase().includes(name.toLowerCase()) ||
    name.toLowerCase().includes(i.name.toLowerCase())
  )

  return partialMatch || null
}

/**
 * Create new item - OFFLINE FIRST
 * Always saves locally first, then syncs to Firebase silently when online
 */
export async function createItem(itemData: Omit<Item, 'id' | 'createdAt' | 'updatedAt'>): Promise<Item | null> {
  const companyId = getCurrentCompanyId()
  if (!companyId) {
    console.warn('[createItem] Missing companyId - cannot create item')
    return null
  }

  const dedupKey = buildItemCreateDedupKey(itemData as any, companyId)
  purgeRecentCreatedItems()

  const recent = recentCreatedItems.get(dedupKey)
  if (recent && Date.now() - recent.timestamp < ITEM_CREATE_DEDUP_WINDOW_MS) {
    return recent.item
  }

  const existingInFlight = inFlightItemCreateKeys.get(dedupKey)
  if (existingInFlight) {
    return existingInFlight
  }

  const promise = createItemUnsafe(itemData)
  inFlightItemCreateKeys.set(dedupKey, promise)
  try {
    const created = await promise
    if (created) {
      recentCreatedItems.set(dedupKey, { item: created, timestamp: Date.now() })
    }
    return created
  } finally {
    inFlightItemCreateKeys.delete(dedupKey)
  }
}

async function createItemUnsafe(itemData: Omit<Item, 'id' | 'createdAt' | 'updatedAt'>): Promise<Item | null> {
  console.log('[createItem] Starting offline-first creation...')

  const now = new Date().toISOString()
  const id = generateOfflineId()
  const companyId = getCurrentCompanyId()

  if (!companyId) {
    console.warn('[createItem] Missing companyId - cannot create item')
    return null
  }

  // Deep clean undefined values (Firestore doesn't accept undefined)
  const cleanData = removeUndefinedDeep(itemData)

  const newItem: Item = {
    ...cleanData,
    id,
    createdAt: now,
    updatedAt: now,
    // Multi-tenant: Add companyId for data isolation
    ...(companyId ? { companyId } : {}),
    // Offline-first metadata
    _pendingSync: !isDeviceOnline(),
    _savedAt: now,
    _syncedAt: null
  } as Item

  // We'll return this. If Firebase sync succeeds, this becomes the synced (server) item.
  let result: Item = newItem

  // STEP 1: Always save to IndexedDB first (instant, never fails)
  try {
    await saveToOffline(STORES.ITEMS, newItem)
    // Also save to localStorage for backward compatibility
    saveItemToLocalStorage(newItem)
    console.log('[createItem] ✅ Item saved locally:', id)
  } catch (error) {
    console.error('[createItem] Failed to save locally:', error)
    // Still try to continue with localStorage only
    saveItemToLocalStorage(newItem)
  }

  // STEP 2: If online, sync to Firebase silently
  if (isDeviceOnline() && isFirebaseReady()) {
    try {
      // Remove offline metadata before sending to Firebase
      const cleanServerData = removeUndefinedDeep({
        ...itemData,
        // Multi-tenant: Add companyId for data isolation
        ...(companyId ? { companyId } : {})
      })
      const serverData = {
        ...cleanServerData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }

      const docRef = await addDoc(collection(db!, COLLECTIONS.ITEMS), serverData)

      // Update local item with Firebase ID and mark as synced
      const syncedItem: Item = {
        ...newItem,
        id: docRef.id,
        _pendingSync: false,
        _syncedAt: now
      } as Item

      // Remove old offline ID record and save with new Firebase ID
      await deleteFromOffline(STORES.ITEMS, id)
      await saveToOffline(STORES.ITEMS, syncedItem)

      // Update localStorage too
      deleteItemFromLocalStorage(id)
      saveItemToLocalStorage(syncedItem)

      console.log('[createItem] ✅ Synced to Firebase:', docRef.id)
      result = syncedItem
    } catch (error) {
      console.warn('[createItem] Firebase sync failed, queuing for later:', error)
      // Add to sync queue for later retry
      await addToSyncQueue('create', STORES.ITEMS, newItem)
    }
  } else {
    // Offline - add to sync queue for later
    console.log('[createItem] 📱 Offline mode - queuing for sync')
    await addToSyncQueue('create', STORES.ITEMS, newItem)
  }

  // Invalidate cache after creating
  invalidateCacheByPrefix('items')

  emitDataEvent(DATA_EVENTS.ITEMS_CHANGED, { action: 'create', id: result.id })

  return result
}

/**
 * Update existing item - OFFLINE FIRST
 * Always saves locally first, then syncs to Firebase silently when online
 */
export async function updateItem(id: string, updates: Partial<Item>): Promise<boolean> {
  const now = new Date().toISOString()

  // STEP 1: Get existing item from local
  let existingItem: Item | null = null
  try {
    existingItem = await getFromOffline<Item>(STORES.ITEMS, id)
  } catch (error) {
    const localItems = getItemsFromLocalStorage()
    existingItem = localItems.find(i => i.id === id) || null
  }

  if (!existingItem) {
    console.error('[updateItem] Item not found:', id)
    return false
  }

  const updatedItem: Item = {
    ...existingItem,
    ...updates,
    updatedAt: now,
    _pendingSync: !isDeviceOnline() || isOfflineId(id),
    _savedAt: now
  } as Item

  // STEP 2: Always save to IndexedDB first (instant)
  try {
    await saveToOffline(STORES.ITEMS, updatedItem)
    updateItemInLocalStorage(id, updatedItem)
    console.log('[updateItem] ✅ Updated locally:', id)
  } catch (error) {
    console.error('[updateItem] Local save failed:', error)
    updateItemInLocalStorage(id, updatedItem)
  }

  // STEP 3: If online and not an offline-only record, sync to Firebase
  if (isDeviceOnline() && isFirebaseReady() && !isOfflineId(id)) {
    try {
      const serverData = removeUndefinedDeep({
        ...updates,
        updatedAt: now
      })
      const docRef = doc(db!, COLLECTIONS.ITEMS, id)
      await updateDoc(docRef, serverData)

      // Mark as synced
      updatedItem._pendingSync = false
      updatedItem._syncedAt = now
      await saveToOffline(STORES.ITEMS, updatedItem)

      console.log('[updateItem] ✅ Synced to Firebase:', id)
    } catch (error) {
      console.warn('[updateItem] Firebase sync failed, queuing:', error)
      await addToSyncQueue('update', STORES.ITEMS, updatedItem)
    }
  } else if (!isOfflineId(id)) {
    // Only queue if it has a Firebase ID (not offline-created)
    await addToSyncQueue('update', STORES.ITEMS, updatedItem)
  }

  // Invalidate cache after update
  invalidateCacheByPrefix('items')
  emitDataEvent(DATA_EVENTS.ITEMS_CHANGED, { action: 'update', id })

  return true
}

/**
 * Delete item - OFFLINE FIRST
 * Always deletes locally first, then syncs to Firebase silently when online
 */
export async function deleteItem(id: string): Promise<boolean> {
  // STEP 1: Always delete from local first (instant)
  try {
    await deleteFromOffline(STORES.ITEMS, id)
    deleteItemFromLocalStorage(id)
    console.log('[deleteItem] ✅ Deleted locally:', id)
  } catch (error) {
    console.error('[deleteItem] Local delete failed:', error)
    deleteItemFromLocalStorage(id)
  }

  // STEP 2: If online and has Firebase ID, sync deletion
  if (isDeviceOnline() && isFirebaseReady() && !isOfflineId(id)) {
    try {
      const docRef = doc(db!, COLLECTIONS.ITEMS, id)
      await deleteDoc(docRef)
      console.log('[deleteItem] ✅ Deleted from Firebase:', id)
    } catch (error) {
      console.warn('[deleteItem] Firebase delete failed, queuing:', error)
      await addToSyncQueue('delete', STORES.ITEMS, { id })
    }
  } else if (!isOfflineId(id)) {
    // Queue for sync if has Firebase ID but currently offline
    await addToSyncQueue('delete', STORES.ITEMS, { id })
  }
  // If it's an offline-only record, no need to sync deletion

  // Invalidate cache after delete
  invalidateCacheByPrefix('items')
  emitDataEvent(DATA_EVENTS.ITEMS_CHANGED, { action: 'delete', id })

  return true
}

/**
 * Update item stock
 */
export async function updateItemStock(id: string, quantity: number, operation: 'add' | 'subtract'): Promise<boolean> {
  console.log(`📦 updateItemStock called: id=${id}, qty=${quantity}, op=${operation}`)
  
  const item = await getItemById(id)
  if (!item) {
    console.log(`❌ Item not found with id: ${id}`)
    return false
  }

  const currentStock = item.stock || 0
  const newStock = operation === 'add'
    ? currentStock + quantity
    : currentStock - quantity

  console.log(`📊 Stock change: ${currentStock} ${operation === 'add' ? '+' : '-'} ${quantity} = ${Math.max(0, newStock)}`)
  
  const success = await updateItem(id, { stock: Math.max(0, newStock) })
  console.log(`✅ updateItem result: ${success ? 'SUCCESS' : 'FAILED'}`)
  
  return success
}

/**
 * Find or create item from scanned invoice data
 */
export async function findOrCreateItemFromInvoice(
  description: string,
  hsnCode?: string,
  rate?: number,
  unit?: string,
  taxRate?: number
): Promise<Item | null> {
  // Try to find by HSN code first
  if (hsnCode) {
    const existing = await findItemByHSN(hsnCode)
    if (existing) {
      // Update price if provided and different
      if (rate && rate !== existing.purchasePrice) {
        await updateItem(existing.id, {
          purchasePrice: rate,
          // Update selling price with a margin (e.g., 20%)
          sellingPrice: rate * 1.2
        })
      }
      return existing
    }
  }

  // Try to find by name
  const existingByName = await findItemByName(description)
  if (existingByName) {
    // Update HSN code if not present
    if (hsnCode && !existingByName.hsnCode) {
      await updateItem(existingByName.id, { hsnCode })
    }
    return existingByName
  }

  // Create new item
  const newItemData: Omit<Item, 'id' | 'createdAt' | 'updatedAt'> = {
    name: description,
    description: description,
    itemCode: `ITEM${Date.now()}`,
    hsnCode: hsnCode || '',
    category: 'General',
    unit: (unit as any) || 'PCS',
    purchasePrice: rate || 0,
    sellingPrice: rate ? rate * 1.2 : 0, // 20% margin
    taxPreference: taxRate && taxRate > 0 ? 'taxable' : 'non-taxable',
    tax: {
      cgst: taxRate ? taxRate / 2 : 0,
      sgst: taxRate ? taxRate / 2 : 0,
      igst: taxRate || 0,
      cess: 0
    },
    stock: 0,
    minStock: 0,
    maxStock: 0,
    reorderPoint: 0,
    isActive: true
  }

  return await createItem(newItemData)
}

// Helper function to convert Firestore document to Item
function docToItem(doc: QueryDocumentSnapshot<DocumentData>): Item {
  const data = doc.data()
  return {
    ...data,
    id: doc.id
  } as Item
}

/**
 * Sync callback for pending item writes created by offline-first saves.
 */
async function syncItemToServer(syncItem: any): Promise<boolean> {
  if (syncItem.store !== STORES.ITEMS) return false
  if (!syncItem.data || !isFirebaseReady()) return false

  const now = new Date().toISOString()
  const item = syncItem.data as Item

  try {
    if (syncItem.type === 'delete') {
      if (!isOfflineId(item.id)) {
        await deleteDoc(doc(db!, COLLECTIONS.ITEMS, item.id))
      }
      return true
    }

    const serverData = removeUndefinedDeep({
      ...item,
      companyId: getCurrentCompanyId() || (item as any).companyId
    })
    delete serverData.id
    delete serverData._pendingSync
    delete serverData._savedAt
    delete serverData._syncedAt

    if (syncItem.type === 'create' || isOfflineId(item.id)) {
      const originalId = item.id
      const docRef = await addDoc(collection(db!, COLLECTIONS.ITEMS), {
        ...serverData,
        createdAt: (item as any).createdAt || serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      const syncedItem = {
        ...item,
        id: docRef.id,
        _pendingSync: false,
        _syncedAt: now
      } as Item

      await deleteFromOffline(STORES.ITEMS, originalId)
      deleteItemFromLocalStorage(originalId)
      await saveToOffline(STORES.ITEMS, syncedItem)
      saveItemToLocalStorage(syncedItem)
    } else if (syncItem.type === 'update') {
      await updateDoc(doc(db!, COLLECTIONS.ITEMS, item.id), {
        ...serverData,
        updatedAt: serverTimestamp()
      })
      await saveToOffline(STORES.ITEMS, {
        ...item,
        _pendingSync: false,
        _syncedAt: now
      } as Item)
    }

    invalidateCacheByPrefix('items')
    emitDataEvent(DATA_EVENTS.ITEMS_CHANGED, { action: 'sync', id: item.id })
    return true
  } catch (error) {
    console.error('Failed to sync item to server:', error)
    return false
  }
}

registerSyncCallback(syncItemToServer)

// ============================================
// LOCAL STORAGE FALLBACK FUNCTIONS
// ============================================

function getItemsFromLocalStorage(): Item[] {
  try {
    const companyId = getCurrentCompanyId()
    if (!companyId) return []

    const stored = getLocalJSON<Item[]>(getLocalStorageKey(), [])
    let items: Item[] = Array.isArray(stored) ? stored.slice() : []
    items = items.filter(item => (item as any).companyId === companyId)

    // Sort by createdAt descending (newest first)
    items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    return items
  } catch (error) {
    console.error('Error reading items from local storage:', error)
    return []
  }
}

function saveItemToLocalStorage(item: Item): void {
  try {
    const storageKey = getLocalStorageKey()
    const items = getMutableLocalJSON<Item[]>(storageKey, [])
    if (!Array.isArray(items)) return
    const index = items.findIndex(existing => existing.id === item.id)
    if (index >= 0) {
      items[index] = { ...items[index], ...item }
    } else {
      items.push(item)
    }
    markLocalJSONDirty(storageKey, { immediate: true })
  } catch (error) {
    console.error('Error saving item to local storage:', error)
  }
}

function updateItemInLocalStorage(id: string, updates: Partial<Item>): boolean {
  try {
    const storageKey = getLocalStorageKey()
    const items = getMutableLocalJSON<Item[]>(storageKey, [])
    if (!Array.isArray(items)) return false
    const index = items.findIndex(i => i.id === id)

    if (index === -1) return false

    items[index] = { ...items[index], ...updates }
    markLocalJSONDirty(storageKey, { immediate: true })
    return true
  } catch (error) {
    console.error('Error updating item in local storage:', error)
    return false
  }
}

function deleteItemFromLocalStorage(id: string): boolean {
  try {
    const storageKey = getLocalStorageKey()
    const items = getMutableLocalJSON<Item[]>(storageKey, [])
    if (!Array.isArray(items)) return false
    const idx = items.findIndex(i => i.id === id)
    if (idx === -1) return false
    items.splice(idx, 1)
    markLocalJSONDirty(storageKey, { immediate: true })
    return true
  } catch (error) {
    console.error('Error deleting item from local storage:', error)
    return false
  }
}
